import { forwardRef, type ComponentProps } from 'react'
import { useLocale } from 'next-intl'
import { createNavigation } from 'next-intl/navigation'
import NextLink from 'next/link'
import { redirect as nextRedirect } from 'next/navigation'

import { isDefaultLocaleHomepage } from './homepage-href'
import { needsNoLocalePrefix } from './locale-prefix'
import { defaultLocale } from './locales'
import { routing } from './routing'

const nav = createNavigation(routing)

// `usePathname` only reads the current path, so it is re-exported unchanged.
export const { usePathname } = nav

// The shared default-locale-homepage decision (consumed by all four helpers); the
// `useRouter` wrapper lives in a `'use client'` module because it needs the
// client-only framework router — re-exported here so `@/i18n/navigation` stays the
// single import surface.
export { isDefaultLocaleHomepage } from './homepage-href'
export { useRouter } from './navigation-router'

type IntlLinkProps = ComponentProps<typeof nav.Link>

export const Link = forwardRef<HTMLAnchorElement, IntlLinkProps>(function Link(
    { href, locale, ...rest },
    ref,
) {
    const activeLocale = useLocale()

    // Two families bypass next-intl's prefixing, both because prefixing would
    // produce a URL that redirects: the default-locale homepage (rendered as the
    // bare `/`, below) and an href that is already final — locale-prefixed, or on
    // a locale-free surface — which renders verbatim. Everything else keeps
    // next-intl's prefixing.
    if (isDefaultLocaleHomepage(href, locale ?? activeLocale) || needsNoLocalePrefix(href)) {
        // next-intl and next/link resolve slightly different React anchor-attribute
        // types (e.g. the `popover` union), so narrow `rest` to next/link's props.
        const nextLinkProps = rest as Omit<ComponentProps<typeof NextLink>, 'href'>
        return (
            <NextLink
                ref={ref}
                href={href as ComponentProps<typeof NextLink>['href']}
                {...nextLinkProps}
            />
        )
    }

    return <nav.Link ref={ref} href={href} locale={locale} {...rest} />
})

export const getPathname: typeof nav.getPathname = (args) => {
    // An already-final href is its own pathname — see `needsNoLocalePrefix`. String
    // form only: a `UrlObject` would need its query and hash re-serialized, and
    // no caller passes one for these surfaces.
    if (typeof args.href === 'string' && needsNoLocalePrefix(args.href)) {
        return args.href
    }

    const pathname = nav.getPathname(args)

    if (isDefaultLocaleHomepage(args.href, args.locale)) {
        const bare = pathname.replace(new RegExp(`^/${defaultLocale}(?=/|\\?|$)`), '')
        return bare === '' || bare.startsWith('?') ? `/${bare}` : bare
    }

    return pathname
}

export const redirect: typeof nav.redirect = (args, type) => {
    // Default-locale homepage → the bare `/` (via the framework redirect, which is a
    // server function and does not prefix); everything else keeps next-intl's
    // locale-aware redirect.
    if (isDefaultLocaleHomepage(args.href, args.locale)) {
        nextRedirect('/', type)
    }
    return nav.redirect(args, type)
}
